/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-namespace */
import {FieldSettingsModel} from "@syncfusion/ej2-dropdowns";
import {CalendarPrioritySumResource} from "./calendar-types/calendarPrioritySumResource";
import {CalendarEventPriority} from "./calendar-types/calendarEventPriority";
import {SettingsMenuItemType} from "./settings-types/settingsMenuItemType";
import {FileStorageResource} from "./settings-types/fileStorageResource";

export namespace TYPE {
  export type Languages = Record<string, Record<string, string>>;

  export interface ReminderUpdate {
    id: number; // Deprecated: no longer used for current-user identity reads (use ProfileService.profile().user.id instead).
    type: string;
    title: string;
    description: string;
    style: string;
    payload: any;
  }

  export interface FieldSettingsModelWithId extends FieldSettingsModel {
    id: number | string;
    name?: string | null;
    description?: string | null;
    count?: number | null;
    image?: string | null;
    type?: string | null;
    push?: boolean | null;
    admin?: boolean | null;
  }

  export interface LocalDocument {
    path: string;
    type: 'image' | 'video' | 'file';
    filename: string;
    size: number;
    localFile?: File;
  }

  export interface Folder {
    id: number;
    name: string;
    count: number;
    parent_id: number;
    documents?: FileStorageResource[];
    folders?: Folder[];
  }

  export interface SsoOption {
    type: string;
    button_label: string;
    auth_url?: string;
    redirect_url?: string;
  }

  export interface UserListItem {
    id: number;
    avatar: string;
    first_name: string;
    last_name: string;
    is_chat_allowed?: boolean;
    admin?: boolean;
  }

  export interface UserDetail {
    activated: boolean;
    activated_at: Date;
    avatar: string;
    company: string;
    created_at: Date;
    email: string;
    first_name: string;
    full_name: string;
    groups: GroupListItem[];
    id: number;
    info_01: string;
    info_02: string;
    info_03: string;
    info_04: string;
    info_05: string;
    info_06: string;
    info_07: string;
    info_08: string;
    info_09: string;
    info_10: string;
    info_11: string;
    is_mail_notify: boolean;
    last_login: Date;
    last_name: string;
    mobile: string;
    permissions: Record<string, number>,
    subtitle: string;
    teamleader_id: number;
    telephone: string;
    title: string;
    user_name: string;
  }

  export interface GroupListItem {
    id: number;
    description: string;
    count: number;
    image: string;
    name: string;
    admin?: boolean;
    fixed?: boolean;
  }

  export interface GroupDetail {
    description: string;
    id: number;
    image: string;
    name: string;
    permissions: Record<string, number>
    users: UserListItem[];
  }

  export interface RecurrenceRule {
    repeatType: 'never' | 'daily' | 'weekly' | 'monthly' | 'yearly';
    repeatEvery?: number;
    repeatEndType?: 'never' | 'until' | 'count';
    repeatDaysUntil?: Date | null;
    repeatDaysCount?: number | null;
    repeatOn?: string[]; // e.g., ['MO', 'WE']
    monthlyRepeatMode?: boolean;
    monthlyRepeatDay?: number;
    monthlyOrdinal?: '1' | '2' | '3' | '4' | 'last';
    monthlyWeekday?: 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';
    repeatMonth?: number; // 1 = Jan, 12 = Dec
  }

  export interface WidgetCalendarEvent {
    date: Date;
    label: string;
    day: number;
    event: CalendarPrioritySumResource;
    eventDots: CalendarEventPriority[];
  }

  export interface ModuleConfiguration {
    type: SettingsMenuItemType | null; // if type is null it is only an internal module to the application and not related to a menu item
    right: string;
    icon: string;
    color: 'default' | 'lightpurple' | 'pink' | 'orange' | 'aqua' | 'yellow' | 'blue' | 'darkorange' | 'cyan' | 'purple' | 'darkgreen' | 'skyblue' | 'red' | 'brightgreen' | 'asphalt' | 'darkblue';
    orientation: 'bottomRight' | 'topRight' | 'bottomLeft' | 'topLeft';
    name: string; // default name
    route: string; // route to the module
    isLegacy: boolean; // if true, the module is in the old desktop app
    isNewsSource: boolean; // if true, the module can provide news for the newsfeed on the home page
    adminOnly: boolean; // if true, the module is only visible for admins
  }

  export interface UserMenuItem extends ModuleConfiguration{
    type: SettingsMenuItemType;
  }

  export interface HomeIcon extends ModuleConfiguration {
    type: SettingsMenuItemType;
    is_active_desktop: boolean;
    is_active_mobile: boolean;
  }

  export interface Profile {
    activated: boolean;
    avatar: string;
    company: string;
    customer_id: number;
    devices: [
      {
        id: number;
        name: string;
      }
    ];
    email: string;
    first_name: string;
    full_name: string;
    groups: GroupListItem[];
    id: number;
    info_01: string;
    info_02: string;
    info_03: string;
    info_04: string;
    info_05: string;
    info_06: string;
    info_07: string;
    info_08: string;
    info_09: string;
    info_10: string;
    info_11: string;
    is_mail_notify: boolean;
    is_trans: boolean;
    last_name: string;
    locale: string;
    mobile: string;
    password: string;
    settings: Record<string, any>;
    telephone: string;
    title: string;
    updated_at: Date;
    user_name: string;
    push_disable_all: boolean;
    push_disable_mobile: boolean;
    push_disable_email: boolean;
    push_disable_browser: boolean;
    push_disable_chat: boolean;
    subtitle: string;
  }

  export interface FeedbackListItem {
    id?: number;
    jsuid?: number;
    is_editable?: boolean;
    is_mandatory: boolean;
    is_anonymous: boolean;
    is_public: boolean;
    is_incomplete?: boolean;
    is_repeating: boolean;
    media: FileStorageResource | null;
    title: string;
    description: string;
    invitation_message?: string;
    date_employee_start?: Date;
    date_employee_deadline?: Date | null;
    date_employee_next?: Date;
    repeat_type?: string;
    repeat_until?: Date | null;
    days_before_deadline?: number;
    hours_before_deadline?: number;
    reminder_message?: string;
    display_style?: 'one_at_a_time' | 'multiple_cards' | 'single_form';
    is_display_question_number?: boolean;
    is_link?: boolean;
    progress?: number;
    questions_count?: number;
    user_id?: number;
    user_name?: string;
    user_avatar?: string;
    completed_at?: Date;
    is_send_push?: boolean;
    is_notify_completions?: boolean;
    status?: string;
    is_succeeded?: boolean;
    files?: FileStorageResource[];
    recipients?: 'all' | 'selection';
    created_at?: Date;
    participants?: number;
    answers_count?: number;
    questions?: FeedbackQuestionItem[];
    users?: UserListItem[];
    groups?: GroupListItem[];
  }

  export interface FeedbackQuestionItem {
    id?: number;
    is_disabled: boolean;
    is_mandatory: boolean;
    is_show_comment: boolean;
    type: 'radio' | 'checkbox' | 'image' | 'slider' | 'rating' | 'textarea' | 'dropdown' | 'order' | 'datetime' | 'time' | 'date' | 'file' | 'points' | 'end';
    files: FileStorageResource[];
    answer_time: number;
    title: string;
    description: string;
    answers: FeedbackQuestionAnswerItem[];
    is_points_branch: boolean;
    points_value: number;
    is_points_success_label: boolean;
    is_points_failure_label: boolean;
    is_points_in_percentage: boolean;
    is_points_success_text: boolean;
    is_points_failure_text: boolean;
    points_success_description: string;
    points_failure_description: string;
    is_valid?: boolean;
  }

  export interface FeedbackQuestionAnswerItem {
    id?: number;
    is_right_answer: boolean;
    is_requires_comment: boolean;
    is_use_value_as_points: boolean;
    points: number;
    label: string;
    title: string;
    value: string | number | boolean;
    files: FileStorageResource[];
    logic_rule: string;
    logic_value: string;
    logic_jump: number;
    image?: FileStorageResource | null;
    slider_minimum: number;
    slider_maximum: number;
    slider_step: number;
    slider_large_step_marker: number;
    slider_small_step_marker: number;
    slider_start_value: number;
    slider_show_small_ticks: boolean;
    slider_show_tooltip: boolean;
    slider_unit: string,
    slider_unit_position_left: boolean;
    slider_is_use_as_points: boolean;
    rating_show_label: boolean;
    rating_allow_reset: boolean;
    rating_half_step: boolean;
  }

  export interface FeedbackStatsItem {
    completion_rate: number;
    participants_count_total: number;
    participants_count_complete: number;
    total_points_possible: number;
    total_points_achieved: number;
    correct_answers_rate: number;
    score_logic_value: number;
    score_logic_my_value: number;
    score_logic_over_rate: number;
    score_logic_under_rate: number;
    questions_count_complete: number;
    questions_count_total: number;
    questions_required_count_complete: number;
    questions_required_count_total: number;
    questions: FeedbackStatsQuestionItem[];
  }

  export interface FeedbackStatsQuestionItem {
    id: number;
    type: 'radio' | 'checkbox' | 'image' | 'slider' | 'rating' | 'textarea' | 'dropdown' | 'order' | 'datetime' | 'time' | 'date' | 'file' | 'points';
    title: string;
    description: string;
    is_mandatory: boolean;
    files: FileStorageResource[];
    dataSource: { x: string; y: number }[] | { date: string; count: number; percentage: number; me: number }[];
    answers: FeedbackStatsQuestionAnswerItem[];
    has_public_stats: boolean;
    public_total_answer_string: string;
    public_total_points_string: string;
    public_correct_answers_string: string;
    comment?: string;
    answer_time?: number;
  }

  export interface FeedbackStatsQuestionAnswerItem {
    id: number;
    is_right_answer: boolean;
    is_my_answer: boolean;
    points: number;
    percentage: number;
    my_percentage: number;
    title: string;
    image?: FileStorageResource | null;
    files: FileStorageResource[];
    slider_minimum: number;
    slider_maximum: number;
    slider_step: number;
    slider_large_step_marker: number;
    slider_small_step_marker: number;
    slider_show_small_ticks: boolean;
    slider_show_tooltip: boolean;
    slider_unit: string,
    slider_unit_position_left: boolean;
    rating_show_label: boolean;
    rating_half_step: boolean;
    value: number;
  }

  export interface FeedbackPassedFaileds {
    id: number;
    avatar: string;
    read_at?: Date;
    user_name: string;
    subtitle?: string;
  }

  export interface FeedbackUser {
    feedback: FeedbackListItem;
    user: unknown;
  }

  export interface WorkflowListItem {
    id?: number;
    jsuid?: number;
    is_editable?: boolean;
    is_mandatory: boolean;
    is_anonymous: boolean;
    is_public: boolean;
    is_incomplete?: boolean;
    is_repeating: boolean;
    media: FileStorageResource | null;
    title: string;
    description: string;
    invitation_message?: string;
    date_employee_start?: Date;
    date_employee_deadline?: Date | null;
    date_employee_next?: Date;
    repeat_type?: string;
    repeat_until?: Date | null;
    days_before_deadline?: number;
    hours_before_deadline?: number;
    reminder_message?: string;
    display_style?: 'one_at_a_time' | 'multiple_cards' | 'single_form';
    is_display_question_number?: boolean;
    is_link?: boolean;
    progress?: number;
    questions_count?: number;
    user_id?: number;
    user_name?: string;
    user_avatar?: string;
    completed_at?: Date;
    canceled_at?: Date;
    expired_at?: Date;
    expires_at?: Date;
    is_send_push?: boolean;
    is_notify_completions?: boolean;
    status?: string;
    is_succeeded?: boolean;
    files?: FileStorageResource[];
    recipients?: 'all' | 'selection';
    created_at?: Date;
    participants?: number;
    answers_count?: number;
    stages?: WorkflowStageItem[];
    users?: UserListItem[];
    groups?: GroupListItem[];
    elapsedDays?: number;
    instancesTotal?: number;
    instancesString?: string;
    completionTime?: string;
    stageStep?: string;
    stageProgress?: number;
    stageName?: string;
    stageDeadline?: Date
  }

  export interface WorkflowStageItem {
    title: string;
    description: string;
    questions?: FeedbackQuestionItem[];
    users?: UserListItem[];
    groups?: GroupListItem[];
    emails?: string[];
    recipients: 'all' | 'requester' | 'selection';
    display_style: 'single_form';
    is_editable: boolean;
    created_at?: Date;
    requester?: string;
    is_display_question_number: boolean;
  }

  export interface CommunityListItem {
    id?: number;
    title: string;
    description: string;
    parent_id: number;
    parent_title: string;
    media: FileStorageResource | null;
    is_abo: boolean;
    is_applicant: boolean;
    is_editable: boolean;
    is_listed: boolean;
    is_private: boolean;
    is_unread: boolean;
    posts: number;
    subscribers: number;
    type: 'channel' | 'folder' | 'discussion';
    breadcrumb: BreadcrumbItem[];
    topics: any[];
    subpages: CommunityListItem[];
    notificationType: string;
    notificationMail: boolean;
    notificationPush: boolean;
    hasSubs: boolean;
  }

  export interface CommunityDetail {
    abos: number;
    applicants: number;
    description: string;
    entries: CommunityDetailEntry[];
    followers: 'participant';
    groups: GroupListItem[];
    id?: number;
    image: FileStorageResource | null;
    is_abo: boolean;
    is_applicant: boolean;
    is_commentable: boolean;
    is_editable: boolean;
    is_listed: boolean;
    is_private: boolean;
    is_push: boolean;
    is_unread: boolean;
    is_writeable: boolean;
    likes: 'participant';
    parent_id: number;
    parent_title: string;
    posts: number;
    title: string;
    type: 'channel';
    user_id: number;
    users: UserListItem[];
    breadcrumb: BreadcrumbItem[];
  }

  export interface CommunityDetailEntry {
    id?: number;
    media: FileStorageResource[];
    images: FileStorageResource[];
    videos: FileStorageResource[];
    files: FileStorageResource[];
    channel_id: number;
    count_comments: number;
    count_likes: number;
    parent_id: number;
    created_at: Date;
    is_editable: boolean;
    is_liked: boolean;
    is_unread: boolean;
    last_comments: any[];
    likes: CommunityDetailEntryLike[];
    message_web: string;
    reply_count: number;
    reply_id: null | number;
    title: string;
    user_avatar: string;
    user_id: number;
    user_lang: string;
    user_name: string;
    user_subtitle: string;
    tooltip_likes: string;
    tooltip_comments: string;
  }

  export interface CommunityDetailEntryLike {
    avatar: string;
    first_name: string;
    full_name: string;
    id: number;
    last_name: string;
    subtitle: string;
    read_at?: Date;
  }

  export interface BreadcrumbItem {
    id: number;
    parent_id: number;
    title: string;
  }

  export interface ExtendedFieldSettingsModel extends FieldSettingsModel {
    /** URL or relative path to an image */
    image?: string;
    /** Google Material icon name*/
    icon?: string;
  }

  export enum KnowledgebaseCategoryContentFilter {
    ARTICLES_ONLY = 'articles_only',
    FILES_ONLY = 'files_only',
  }

  export enum KnowledgebaseFilterType {
    ARTICLE = 'article',
    CATEGORY = 'category',
    FILE = 'file',
  }

  export enum KnowledgebaseFileViewType {
    LIST = 'list',
    GRID = 'grid',
  }

  export enum KnowledgebaseCategoryOrder {
    RECENT = 'recent',
    ALPHABETICAL = 'alphabetical',
    IMPORTANCE = 'importance',
    PREDEFINED = 'predefined',
  }

  export enum KnowledgebaseCategoryContentOrder {
    PREDEFINED = 'predefined',
    ALPHABETICAL = 'alphabetical',
    CREATION_DATE = 'recent',
  }

  export enum FirebaseNotificationsState {
    INITIALIZING = 'initializing', // initial state before checking permissions and service worker
    UNDEFINED = 'undefined', // unknown browser support
    ERROR = 'error', // an error occurred while setting up notifications
    UNSUPPORTED = 'unsupported', // service workers or notifications not supported by the browser
    GRANTED = 'granted', // user granted permission
    DENIED = 'denied', // user denied permission
    DEFAULT = 'default', // user dismissed permission prompt without making a choice
  }
}
